import { MovableDialog } from '@/components/shared/MovableDialog';
import { useCallback, useEffect, useMemo, useState, type FormEvent } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  v2,
  type DocumentTemplate,
  type GenerationResult,
  type Submission,
  type TemplateVersion,
} from '@/api/v2Client';
import { ApiErrorNotice } from '@/components/shared/ApiErrorNotice';
import { VersionDiffPanel } from '@/components/admin/form-templates/VersionDiffPanel';

// T-17 D11 — every error site stores the caught value (plus the caller text for
// when nothing presentable can be derived) and renders it through ApiErrorNotice.
// `ApiError.message` is the raw JSON body, so the old `err.message` text printed
// the envelope to the user.
interface PageError {
  error: unknown;
  fallback: string;
}

// `error: undefined` is a local (client-side) message, not a caught failure.
function PageErrorNotice({ e, onRetry }: { e: PageError; onRetry?: () => void }) {
  if (e.error === undefined) {
    return (
      <div role="alert" style={{ color: '#b91c1c', marginTop: 4, fontSize: 13 }}>
        {e.fallback}
      </div>
    );
  }
  return (
    <ApiErrorNotice error={e.error} fallback={e.fallback} {...(onRetry !== undefined && { onRetry })} />
  );
}

interface SubmissionContextDraft {
  productName: string;
  sponsorName: string;
}

export function TemplateDetailPage() {
  const { templateId = '' } = useParams<{ templateId: string }>();

  const [template, setTemplate] = useState<DocumentTemplate | null>(null);
  const [submissions, setSubmissions] = useState<Submission[] | null>(null);
  const [loadError, setLoadError] = useState<PageError | null>(null);

  const [loadKey, setLoadKey] = useState(0);

  const [variableValues, setVariableValues] = useState<Record<string, string>>({});
  const [selectedSubmissionId, setSelectedSubmissionId] = useState<string>('');
  const [showBody, setShowBody] = useState<boolean>(false);
  const [showContext, setShowContext] = useState<boolean>(false);
  const [context, setContext] = useState<SubmissionContextDraft>({
    productName: '',
    sponsorName: '',
  });

  const [generating, setGenerating] = useState<boolean>(false);
  const [generateError, setGenerateError] = useState<PageError | null>(null);
  const [result, setResult] = useState<GenerationResult | null>(null);

  // phase-template-version-history — inline body editor state.
  const [editingBody, setEditingBody] = useState<boolean>(false);
  const [editBodyValue, setEditBodyValue] = useState<string>('');
  const [changeNote, setChangeNote] = useState<string>('');
  const [savingBody, setSavingBody] = useState<boolean>(false);
  const [saveBodyError, setSaveBodyError] = useState<PageError | null>(null);

  // phase-template-version-history — version list state.
  const [versions, setVersions] = useState<TemplateVersion[] | null>(null);
  const [versionsLoading, setVersionsLoading] = useState<boolean>(false);
  const [versionsError, setVersionsError] = useState<PageError | null>(null);
  const [restoreConfirm, setRestoreConfirm] = useState<TemplateVersion | null>(null);
  const [restoring, setRestoring] = useState<boolean>(false);
  // Form Templates row 8 — the version on the "To" side of Compare.
  const [compareRight, setCompareRight] = useState<string | null>(null);

  // Phase template-pdf-preview — letterhead overlay state.
  // undefined = not yet loaded; null = no profile / no letterhead configured;
  // string = the letterhead URL fetched from the first company profile.
  // phase-fix-template-generate-pdf-output — the generated document is now a
  // real PDF; view it inline rather than re-rendering the markdown as HTML.
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [pdfLoading, setPdfLoading] = useState<boolean>(false);
  const [pdfError, setPdfError] = useState<PageError | null>(null);

  // Fetch template + submissions in parallel on mount.
  useEffect(() => {
    if (!templateId) return;
    setTemplate(null);
    setSubmissions(null);
    setLoadError(null);

    Promise.all([v2.documentTemplates.get(templateId), v2.submissions.list()])
      .then(([tpl, subsResult]) => {
        setTemplate(tpl);
        // Phase dashboard-performance-fix — submissions.list now returns
        // { data, pagination }; extract the data array.
        setSubmissions(subsResult.data);

        // Pre-fill the variable form with defaults so the user only has to
        // touch what they want to override.
        const initial: Record<string, string> = {};
        for (const v of tpl.Variables) {
          if (v.defaultValue !== undefined) initial[v.name] = v.defaultValue;
        }
        setVariableValues(initial);
      })
      .catch((err: unknown) => {
        setLoadError({ error: err, fallback: 'Failed to load template or submissions' });
      });
  }, [templateId, loadKey]);

  // phase-fix-template-generate-pdf-output — once a document is generated,
  // fetch its rendered PDF as an object URL for the inline viewer.
  //
  // Fetched via apiFetch (bearer token attached) → blob rather than pointing
  // the iframe at the file URL directly: a bare iframe GET carries no auth
  // header and would 401. The server serves the bytes as octet-stream, so we
  // re-type the blob as application/pdf here so the iframe renders inline
  // instead of downloading. Object URL revoked on change/unmount.
  const generatedDocId = result?.documentId ?? null;
  useEffect(() => {
    if (generatedDocId === null) {
      setPdfUrl(null);
      return;
    }
    let cancelled = false;
    let objectUrl: string | null = null;
    setPdfLoading(true);
    setPdfError(null);
    v2.documents
      .downloadFile(generatedDocId)
      .then(async (res) => {
        const bytes = await res.arrayBuffer();
        if (cancelled) return;
        objectUrl = URL.createObjectURL(new Blob([bytes], { type: 'application/pdf' }));
        setPdfUrl(objectUrl);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setPdfError({ error: err, fallback: 'Could not load the generated PDF.' });
      })
      .finally(() => {
        if (!cancelled) setPdfLoading(false);
      });
    return (): void => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
      setPdfUrl(null);
    };
  }, [generatedDocId]);

  // Download the actual generated PDF (the stored artifact), not the source text.
  const handleDownload = (): void => {
    if (!result || !template || pdfUrl === null) return;
    const a = document.createElement('a');
    a.href = pdfUrl;
    a.download = result.fileName || `${template.Name}.pdf`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const requiredMissing = useMemo(() => {
    if (!template) return [];
    return template.Variables.filter(
      (v) => v.required && !(variableValues[v.name] ?? '').trim(),
    ).map((v) => v.name);
  }, [template, variableValues]);

  const canSubmit =
    template !== null &&
    submissions !== null &&
    selectedSubmissionId !== '' &&
    requiredMissing.length === 0 &&
    !generating;

  const handleVarChange = (name: string, value: string): void => {
    setVariableValues((prev) => ({ ...prev, [name]: value }));
  };

  // phase-template-version-history — fetch version list.
  const fetchVersions = useCallback(async (): Promise<void> => {
    if (!templateId) return;
    setVersionsLoading(true);
    setVersionsError(null);
    try {
      const res = await v2.documentTemplates.versions(templateId);
      setVersions(res);
    } catch (err: unknown) {
      setVersionsError({ error: err, fallback: 'Failed to load versions' });
    } finally {
      setVersionsLoading(false);
    }
  }, [templateId]);

  // phase-template-version-history — save updated body.
  const handleSaveBody = async (): Promise<void> => {
    if (!template) return;
    // phase-form-template-versioning — the change note is required now:
    // every save creates a new version and the server 400s a blank note.
    if (changeNote.trim() === '') {
      setSaveBodyError({
        error: undefined,
        fallback: 'Describe what changed and why — the note is saved with the new version.',
      });
      return;
    }
    setSavingBody(true);
    setSaveBodyError(null);
    try {
      const updated = await v2.documentTemplates.update(template.Id, {
        Body: editBodyValue,
        ChangeNote: changeNote.trim(),
      });
      setTemplate(updated);
      setEditingBody(false);
      setChangeNote('');
      if (versions !== null) void fetchVersions();
    } catch (err: unknown) {
      setSaveBodyError({ error: err, fallback: 'Save failed' });
    } finally {
      setSavingBody(false);
    }
  };

  // phase-template-version-history — restore a prior version.
  const handleRestore = async (version: TemplateVersion): Promise<void> => {
    if (!template) return;
    setRestoring(true);
    setVersionsError(null);
    try {
      const res = await v2.documentTemplates.restore(template.Id, version.Id);
      setTemplate(res);
      setRestoreConfirm(null);
      void fetchVersions();
    } catch (err: unknown) {
      // phase-fix-v2client-envelope-sweep — this used to be a bare
      // `catch { /* ignore */ }`. Combined with the envelope-typed client it
      // meant every restore threw a TypeError that was silently dropped, so
      // the button appeared to do nothing at all. Surface failures on the
      // page's existing versions-error line instead of swallowing them.
      setVersionsError({ error: err, fallback: 'Failed to restore this version' });
    } finally {
      setRestoring(false);
    }
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    if (!template || !canSubmit) return;
    setGenerating(true);
    setGenerateError(null);
    setResult(null);

    // Strip empty optional values so the backend doesn't see "" — it's
    // happy with missing keys (template will leave the placeholder as
    // `{{name}}` and report it in `variablesDefaulted`).
    const variables: Record<string, string> = {};
    for (const v of template.Variables) {
      const val = (variableValues[v.name] ?? '').trim();
      if (val) variables[v.name] = val;
    }

    const submissionContext: Record<string, unknown> = {};
    if (context.productName.trim()) submissionContext['productName'] = context.productName.trim();
    if (context.sponsorName.trim()) submissionContext['sponsorName'] = context.sponsorName.trim();

    try {
      const out = await v2.documentTemplates.generate(template.Id, {
        submissionId: selectedSubmissionId,
        variables,
        ...(Object.keys(submissionContext).length > 0 && { submissionContext }),
      });
      setResult(out);
    } catch (err: unknown) {
      setGenerateError({ error: err, fallback: 'Generation failed' });
    } finally {
      setGenerating(false);
    }
  };

  if (loadError) {
    return (
      <section>
        <Link to="/templates" style={{ color: '#1B3A6B' }}>
          ← Back to templates
        </Link>
        <PageErrorNotice e={loadError} onRetry={() => setLoadKey((k) => k + 1)} />
      </section>
    );
  }

  if (!template || !submissions) {
    return (
      <section>
        <Link to="/templates" style={{ color: '#1B3A6B' }}>
          ← Back to templates
        </Link>
        <p style={{ marginTop: 12 }}>Loading…</p>
      </section>
    );
  }

  return (
    <section>
      <Link to="/templates" style={{ color: '#1B3A6B' }}>
        ← Back to templates
      </Link>

      <header style={{ marginTop: 8, marginBottom: 24 }}>
        <h2 style={{ margin: '0 0 4px 0' }}>{template.Name}</h2>
        <div style={{ color: '#6b7280', fontSize: 14 }}>
          Module <strong>{template.TargetModule}</strong>
          {template.TenantId === null && (
            <span
              style={{
                marginLeft: 12,
                background: '#dbeafe',
                color: '#1e3a8a',
                padding: '2px 8px',
                borderRadius: 4,
                fontSize: 12,
              }}
            >
              Global
            </span>
          )}
        </div>
        {template.Description && (
          <p style={{ marginTop: 8, color: '#374151' }}>{template.Description}</p>
        )}
      </header>

      <details
        open={showBody}
        onToggle={(e) => setShowBody(e.currentTarget.open)}
        style={{ marginBottom: 24, border: '1px solid #e5e7eb', borderRadius: 6 }}
      >
        <summary
          style={{ padding: '8px 12px', cursor: 'pointer', fontWeight: 600, color: '#374151' }}
        >
          Template body ({template.Body.length.toLocaleString()} chars)
        </summary>
        {!editingBody ? (
          <div style={{ padding: 12 }}>
            <pre
              style={{
                margin: 0,
                background: '#f9fafb',
                fontSize: 13,
                overflow: 'auto',
                maxHeight: 320,
                whiteSpace: 'pre-wrap',
              }}
            >
              {template.Body}
            </pre>
            <button
              data-testid="edit-body-btn"
              type="button"
              onClick={() => {
                setEditBodyValue(template.Body);
                setChangeNote('');
                setSaveBodyError(null);
                setEditingBody(true);
              }}
              style={{
                marginTop: 8,
                background: '#fff',
                color: '#1B3A6B',
                border: '1px solid #1B3A6B',
                padding: '6px 14px',
                borderRadius: 6,
                fontSize: 13,
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              Edit body
            </button>
          </div>
        ) : (
          <div style={{ padding: 12 }}>
            <textarea
              data-testid="edit-body-textarea"
              value={editBodyValue}
              onChange={(e) => setEditBodyValue(e.target.value)}
              rows={12}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: '1px solid #d1d5db',
                borderRadius: 6,
                fontSize: 13,
                fontFamily: 'monospace',
                boxSizing: 'border-box',
              }}
            />
            <input
              data-testid="change-note-input"
              type="text"
              placeholder="What changed, and why? (required)"
              value={changeNote}
              onChange={(e) => setChangeNote(e.target.value)}
              style={{
                display: 'block',
                width: '100%',
                marginTop: 8,
                padding: '8px 12px',
                border: '1px solid #d1d5db',
                borderRadius: 6,
                fontSize: 13,
                boxSizing: 'border-box',
              }}
            />
            {saveBodyError && <PageErrorNotice e={saveBodyError} />}
            <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
              <button
                data-testid="save-body-btn"
                type="button"
                disabled={savingBody}
                onClick={() => void handleSaveBody()}
                style={{
                  background: '#1B3A6B',
                  color: '#fff',
                  padding: '6px 14px',
                  border: 'none',
                  borderRadius: 6,
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: savingBody ? 'not-allowed' : 'pointer',
                }}
              >
                {savingBody ? 'Saving…' : 'Save'}
              </button>
              <button
                data-testid="cancel-edit-body-btn"
                type="button"
                onClick={() => setEditingBody(false)}
                style={{
                  background: '#fff',
                  color: '#374151',
                  border: '1px solid #d1d5db',
                  padding: '6px 14px',
                  borderRadius: 6,
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </details>

      <form onSubmit={handleSubmit} aria-label="Generate document">
        <h3 style={{ marginBottom: 12 }}>Submission</h3>
        <div style={{ marginBottom: 24 }}>
          <label htmlFor="submission-picker" style={{ display: 'block', marginBottom: 4 }}>
            Target submission <span style={{ color: '#b91c1c' }}>*</span>
          </label>
          <select
            id="submission-picker"
            value={selectedSubmissionId}
            onChange={(e) => setSelectedSubmissionId(e.target.value)}
            required
            style={{
              padding: '8px 12px',
              border: '1px solid #d1d5db',
              borderRadius: 6,
              fontSize: 14,
              minWidth: 360,
            }}
          >
            <option value="">— pick a submission —</option>
            {submissions.map((s) => (
              <option key={s.Id} value={s.Id}>
                {s.DrugName} · {s.Region} · seq {s.SequenceNumber}
              </option>
            ))}
          </select>
          {submissions.length === 0 && (
            <p style={{ color: '#b45309', marginTop: 4, fontSize: 13 }}>
              No submissions available. Create one before generating.
            </p>
          )}
        </div>

        <h3 style={{ marginBottom: 12 }}>Variables</h3>
        {template.Variables.length === 0 && (
          <p style={{ color: '#6b7280' }}>
            This template has no declared variables. You can generate immediately.
          </p>
        )}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16, marginBottom: 24 }}>
          {template.Variables.map((v) => {
            const isMultiline = (v.description?.length ?? 0) > 60;
            const value = variableValues[v.name] ?? '';
            const id = `var-${encodeURIComponent(v.name)}`;
            const label = v.name.replace(/([a-z])([A-Z])/g, '$1 $2').replace(/[_-]/g, ' ').replace(/^./, (c) => c.toUpperCase());
            return (
              <div key={v.name}>
                <label htmlFor={id} style={{ display: 'block', marginBottom: 4, fontWeight: 600 }}>
                  {label}
                  {v.required && <span style={{ color: '#b91c1c' }}> *</span>}
                </label>
                {v.description && (
                  <p style={{ margin: '0 0 4px 0', fontSize: 13, color: '#6b7280' }}>
                    {v.description}
                  </p>
                )}
                {isMultiline ? (
                  <textarea
                    id={id}
                    value={value}
                    onChange={(e) => handleVarChange(v.name, e.target.value)}
                    rows={3}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      border: '1px solid #d1d5db',
                      borderRadius: 6,
                      fontSize: 14,
                      fontFamily: 'inherit',
                    }}
                  />
                ) : (
                  <input
                    id={id}
                    type="text"
                    value={value}
                    onChange={(e) => handleVarChange(v.name, e.target.value)}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      border: '1px solid #d1d5db',
                      borderRadius: 6,
                      fontSize: 14,
                    }}
                  />
                )}
              </div>
            );
          })}
        </div>

        <details
          open={showContext}
          onToggle={(e) => setShowContext(e.currentTarget.open)}
          style={{ marginBottom: 24 }}
        >
          <summary style={{ cursor: 'pointer', color: '#374151', fontWeight: 600 }}>
            Optional submission context
          </summary>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 12 }}>
            <div>
              <label htmlFor="ctx-product" style={{ display: 'block', marginBottom: 4 }}>
                Product name
              </label>
              <input
                id="ctx-product"
                type="text"
                value={context.productName}
                onChange={(e) =>
                  setContext((c) => ({ ...c, productName: e.target.value }))
                }
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: 6,
                  fontSize: 14,
                }}
              />
            </div>
            <div>
              <label htmlFor="ctx-sponsor" style={{ display: 'block', marginBottom: 4 }}>
                Sponsor name
              </label>
              <input
                id="ctx-sponsor"
                type="text"
                value={context.sponsorName}
                onChange={(e) =>
                  setContext((c) => ({ ...c, sponsorName: e.target.value }))
                }
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: 6,
                  fontSize: 14,
                }}
              />
            </div>
          </div>
        </details>

        <button
          type="submit"
          disabled={!canSubmit}
          style={{
            background: canSubmit ? '#1B3A6B' : '#9ca3af',
            color: '#fff',
            padding: '10px 20px',
            border: 'none',
            borderRadius: 6,
            fontSize: 14,
            fontWeight: 600,
            cursor: canSubmit ? 'pointer' : 'not-allowed',
          }}
        >
          {generating ? 'Generating…' : 'Generate'}
        </button>
        {!canSubmit && requiredMissing.length > 0 && (
          <p style={{ color: '#b45309', marginTop: 8, fontSize: 13 }}>
            Required variable(s) missing: {requiredMissing.join(', ')}
          </p>
        )}
      </form>

      {generateError && (
        <div style={{ marginTop: 24 }}>
          <PageErrorNotice e={generateError} />
        </div>
      )}

      {result && (
        <section style={{ marginTop: 32 }} aria-label="Generated document">
          <h3 style={{ marginBottom: 12, color: '#15803d' }}>
            ✓ Generated successfully
          </h3>
          <dl
            style={{
              display: 'grid',
              gridTemplateColumns: 'max-content 1fr',
              gap: '8px 24px',
              marginBottom: 16,
            }}
          >
            <dt style={{ fontWeight: 600 }}>Document ID</dt>
            <dd>
              <Link
                to={`/submissions/${selectedSubmissionId}`}
                style={{ color: '#1B3A6B', fontFamily: 'monospace' }}
              >
                {result.documentId}
              </Link>
            </dd>
            <dt style={{ fontWeight: 600 }}>File name</dt>
            <dd style={{ fontFamily: 'monospace' }}>{result.fileName}</dd>
            <dt style={{ fontWeight: 600 }}>Model</dt>
            <dd style={{ fontFamily: 'monospace' }}>{result.generation.model}</dd>
            {result.generation.usage && (
              <>
                <dt style={{ fontWeight: 600 }}>Tokens</dt>
                <dd>
                  in {result.generation.usage.inputTokens.toLocaleString()} / out{' '}
                  {result.generation.usage.outputTokens.toLocaleString()}
                </dd>
              </>
            )}
            {result.generation.variablesDefaulted.length > 0 && (
              <>
                <dt style={{ fontWeight: 600 }}>Defaulted</dt>
                <dd style={{ color: '#b45309' }}>
                  {result.generation.variablesDefaulted.join(', ')}
                </dd>
              </>
            )}
          </dl>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: 8,
              gap: 12,
              flexWrap: 'wrap',
            }}
          >
            <h4 style={{ margin: 0 }}>Preview</h4>
            {/* Phase phase-settings-link-injection — show which link style
                is active when the backend ran the autoInjectLinks pass. */}
            {result.linkInjection?.applied && (
              <span
                data-testid="template-preview-link-style-indicator"
                style={{
                  fontSize: 11,
                  padding: '2px 8px',
                  borderRadius: 999,
                  background: result.linkInjection.blueUnderline ? '#dbeafe' : '#f3f4f6',
                  color: result.linkInjection.blueUnderline ? '#1e3a8a' : '#374151',
                  fontWeight: 600,
                  border: '1px solid ' +
                    (result.linkInjection.blueUnderline ? '#93c5fd' : '#d1d5db'),
                }}
              >
                Link style: {result.linkInjection.blueUnderline ? 'blue underlined' : 'invisible'}
              </span>
            )}
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                type="button"
                onClick={handleDownload}
                disabled={pdfUrl === null}
                data-testid="generated-download"
                style={{
                  background: '#fff',
                  color: '#1B3A6B',
                  border: '1px solid #1B3A6B',
                  padding: '6px 14px',
                  borderRadius: 6,
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: pdfUrl === null ? 'not-allowed' : 'pointer',
                  opacity: pdfUrl === null ? 0.5 : 1,
                }}
              >
                Download PDF
              </button>
            </div>
          </div>

          {/* phase-fix-template-generate-pdf-output — the generated document is
              a real PDF now; show that artifact inline, the same way the
              document viewer does, rather than re-rendering the source text. */}
          <div
            data-testid="generated-pdf-pane"
            style={{
              background: '#f3f4f6',
              border: '1px solid #e5e7eb',
              borderRadius: 6,
              minHeight: 480,
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            {pdfLoading && (
              <div
                data-testid="generated-pdf-loading"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flex: 1,
                  minHeight: 480,
                  color: '#6b7280',
                  fontSize: 14,
                }}
              >
                Loading generated PDF…
              </div>
            )}
            {!pdfLoading && pdfError !== null && (
              <div data-testid="generated-pdf-error" style={{ margin: 20 }}>
                <PageErrorNotice e={pdfError} />
              </div>
            )}
            {!pdfLoading && pdfError === null && pdfUrl !== null && (
              <iframe
                src={pdfUrl}
                title={`${template.Name} — generated PDF`}
                data-testid="generated-pdf-frame"
                style={{ width: '100%', height: 480, border: 'none', borderRadius: 6 }}
              />
            )}
          </div>
        </section>
      )}

      <details
        data-testid="version-history-panel"
        style={{ marginTop: 32, border: '1px solid #e5e7eb', borderRadius: 6, padding: '8px 12px' }}
        onToggle={(e) => {
          const isOpen = e.currentTarget.open;
          if (isOpen && !versions) void fetchVersions();
        }}
      >
        <summary style={{ cursor: 'pointer', fontWeight: 600, color: '#374151' }}>
          Version history
        </summary>
        {versionsLoading && <p data-testid="versions-loading">Loading…</p>}
        {versionsError && <PageErrorNotice e={versionsError} />}
        {versions && versions.length === 0 && <p data-testid="versions-empty">No versions yet.</p>}
        {/* Row 8 — compare two versions (default: the chosen one vs the one before it). */}
        {versions && compareRight !== null && template && (
          <div style={{ margin: '8px 0' }}>
            <button
              type="button"
              data-testid="version-compare-close"
              onClick={() => setCompareRight(null)}
              style={{ background: 'none', border: 'none', color: '#6b7280', cursor: 'pointer', fontSize: 12, padding: 0, marginBottom: 6 }}
            >
              Close compare
            </button>
            <VersionDiffPanel key={compareRight} templateId={template.Id} versions={versions} initialRight={compareRight} />
          </div>
        )}
        {versions && versions.map((v) => (
          <div key={v.Id} data-testid={`version-row-${v.Id}`} style={{ display: 'flex', gap: 12, alignItems: 'center', padding: '6px 0', borderTop: '1px solid #f3f4f6' }}>
            <span>v{v.VersionNumber}</span>
            <span>{new Date(v.CreatedAt).toLocaleDateString()}</span>
            <span data-testid={`version-change-note-${v.Id}`}>{v.ChangeNote ?? '—'}</span>
            <button
              data-testid={`compare-btn-${v.Id}`}
              type="button"
              onClick={() => setCompareRight(v.Id)}
              style={{
                background: '#fff',
                color: '#374151',
                border: '1px solid #d1d5db',
                padding: '4px 10px',
                borderRadius: 4,
                fontSize: 12,
                cursor: 'pointer',
              }}
            >
              Compare
            </button>
            <button
              data-testid={`restore-btn-${v.Id}`}
              type="button"
              onClick={() => setRestoreConfirm(v)}
              style={{
                background: '#fff',
                color: '#1B3A6B',
                border: '1px solid #1B3A6B',
                padding: '4px 10px',
                borderRadius: 4,
                fontSize: 12,
                cursor: 'pointer',
              }}
            >
              Restore
            </button>
          </div>
        ))}
      </details>

      {restoreConfirm && (
        <MovableDialog persistKey="templates.restore" testId="restore-confirm-dialog"
          title="Restore template version" ariaLabel="Restore template version" onClose={() => setRestoreConfirm(null)} escDisabled={restoring} width={760}>

            <p>Restore to version {restoreConfirm.VersionNumber}?</p>
            {/* Row 8 — what the restore replaces (current text) vs what it puts back. */}
            {versions && template && (
              <VersionDiffPanel
                templateId={template.Id}
                versions={versions}
                initialLeft="current"
                initialRight={restoreConfirm.Id}
                fromLabel="Replaces"
                toLabel="Restores"
                readOnly
              />
            )}
            <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
              <button
                data-testid="restore-confirm-btn"
                type="button"
                disabled={restoring}
                onClick={() => void handleRestore(restoreConfirm)}
                style={{
                  background: '#1B3A6B',
                  color: '#fff',
                  padding: '8px 16px',
                  border: 'none',
                  borderRadius: 6,
                  fontSize: 14,
                  fontWeight: 600,
                  cursor: restoring ? 'not-allowed' : 'pointer',
                }}
              >
                Confirm
              </button>
              <button
                data-testid="restore-cancel-btn"
                type="button"
                onClick={() => setRestoreConfirm(null)}
                style={{
                  background: '#fff',
                  color: '#374151',
                  border: '1px solid #d1d5db',
                  padding: '8px 16px',
                  borderRadius: 6,
                  fontSize: 14,
                  cursor: 'pointer',
                }}
              >
                Cancel
              </button>
            </div>
        </MovableDialog>
      )}
    </section>
  );
}
